import { Stack, Typography, List, ListItem, Grid } from '@mui/material';

import { useDispatch, useSelector } from 'react-redux';

import DOMPurify from 'dompurify';
import { isEmpty, noop } from 'lodash';

import { ReactComponent as DownloadIcon } from 'common/assets/images/download.svg';
import { ReactComponent as SaveIcon } from 'common/assets/images/save-ai-icon.svg';
import { ReactComponent as ShareIcon } from 'common/assets/images/share-icon.svg';
import AIFeedback from 'common/components/AIFeedback/AIFeedback';
import ButtonComp from 'common/components/Button/ButtonComp';
import PromptSelect from 'common/components/PromptSelect/PromptSelect';
import TextFieldWithLabel from 'common/components/TextFieldWithLabel/TextFieldWithLabel';
import { MODULES } from 'common/constants';
import {
	AI_SIMULATION_GENAI_DISCLAIMER,
	AI_TYPE,
} from 'common/constants/AISimulationConstants';
import { FeatureFlagsKeysEnum } from 'common/constants/FeatureFlags';
import { RootState } from 'common/store/AppStore';
import { setControlStepsData } from 'common/store/reducer/controlTestScriptReducer';
import { toSentenceCase } from 'common/utils/utilFunctions';

import { useFeatureFlag } from 'hooks/useFeatureFlags';

import { mapTitleWithModel } from 'features/AiSimulation/AiSimulationUtil';
import { IGeneratedTextAreaProps } from 'features/AiSimulation/api/type-def';

import styles from './GeneratedTextArea.module.scss';

const GeneratedTextArea = (props: IGeneratedTextAreaProps) => {
	const {
		title,
		category,
		button,
		textValue,
		editable = true,
		dataType = 'input',
		onChange = noop,
		downloadHandler,
		shareHandler,
		saveHandler,
		inputElement,
		isSaveIconVisible,
		isIconDisable,
		isRegenerating,
		inputData,
	} = props;
	const { IS_EXPORT_BUTTON_ENABLED } = FeatureFlagsKeysEnum;
	const { DISCLAIMER } = AI_SIMULATION_GENAI_DISCLAIMER;
	const { MODEL, RECIPE } = AI_TYPE;
	const handleTextAreaChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		onChange(event.target.value);
	};
	const dispatch = useDispatch();

	const isLDResponseExists: boolean = useSelector(
		(state: RootState) => state.common.isLDResponseAvailable,
	);
	const isExportButtonEnabled = useFeatureFlag(
		IS_EXPORT_BUTTON_ENABLED,
		isLDResponseExists,
	);

	const selectedAIModel = useSelector(
		(state: RootState) => state?.simulationArena?.selectedAIModel,
	);

	const selectedAIRecipe = useSelector(
		(state: RootState) => state.simulationArena.selectedAIRecipe,
	);

	const { AI_SIMULATION_AND_EXPERIMENTATION } = MODULES;

	const handleButtonClick = () => {
		dispatch(setControlStepsData([]));
		button?.onClick?.();
	};

	const handlePromptChange = (e: any) => {
		dispatch(setControlStepsData([]));
		button?.onPromptChange?.(e);
	};

	function covertStringToList(content: string) {
		const txtContent = DOMPurify.sanitize(content);
		if (!txtContent.includes('\n')) {
			return <ListItem> {txtContent}</ListItem>;
		}
		const lines = txtContent.split('\n');
		return lines
			.filter((i) => i)
			.map((item) => {
				const match = item.trim().match(/^(\d+\.)(.*)/);
				const startIndex = match?.[1] || '';
				const contentStr = match?.[2] || item;

				return (
					<ListItem className={styles['testscript-steps-layout']} key={item}>
						<Stack
							direction="row"
							justifyContent="space-between"
							sx={{ width: '100%' }}
						>
							<Stack
								direction="row"
								gap={1}
								alignItems="center"
								sx={{ flex: 4 }}
							>
								<Typography>{startIndex}</Typography>
								<Typography>{contentStr}</Typography>
							</Stack>
						</Stack>
					</ListItem>
				);
			});
	}
	const shouldReset = isRegenerating || false;
	const outputActionClassName = !(isEmpty(textValue) || isIconDisable)
		? ''
		: styles.disabled;
	const objectType = mapTitleWithModel[title];
	const getReferenceCondition = () => {
		if (category === MODEL) {
			return selectedAIModel?.id;
		}
		if (category === RECIPE) {
			return selectedAIRecipe?.id;
		}
		return selectedAIModel?.id || selectedAIRecipe?.id;
	};
	return (
		<Stack gap={2} className={styles['section-container']}>
			<Grid
				container
				style={{
					display: 'flex',
					justifyContent: 'space-between',
				}}
			>
				<Grid item>
					<Typography className={styles.title}>
						{toSentenceCase(title)}
					</Typography>
				</Grid>
				<Grid item>
					{dataType === 'output' && (
						<Stack direction="row" gap={1} justifyContent="space-between">
							<ButtonComp
								btnType="secondary"
								data-testid="download-icon"
								size="small"
								disabled={isIconDisable || isExportButtonEnabled}
								clickHandler={downloadHandler}
							>
								<DownloadIcon
									className={
										isExportButtonEnabled
											? styles.disabled
											: `${outputActionClassName} ${styles['download-icon']}`
									}
								/>{' '}
								Download
							</ButtonComp>
							<ButtonComp
								btnType="secondary"
								data-testid="share-btn"
								size="small"
								clickHandler={shareHandler}
								disabled={isIconDisable}
							>
								<ShareIcon
									className={`${outputActionClassName} ${styles['activity-icon-color']}`}
								/>{' '}
								Share
							</ButtonComp>
							{isSaveIconVisible && (
								<ButtonComp
									btnType="secondary"
									data-testid="share-btn"
									size="small"
									clickHandler={saveHandler}
									disabled={isIconDisable}
								>
									<SaveIcon
										className={`${outputActionClassName} ${styles['save-icon-color']}`}
									/>{' '}
									Save
								</ButtonComp>
							)}
						</Stack>
					)}
				</Grid>
			</Grid>

			<Stack>
				{editable ? (
					<TextFieldWithLabel
						inputProps={{
							'data-testid': `gen-textarea-${dataType}`,
						}}
						id={`${title}-textarea`}
						value={textValue}
						onChange={handleTextAreaChange}
						multiLine
						showLabel={false}
						error={false}
						helperText=""
						className={styles.textarea}
					/>
				) : (
					<List
						data-testid={`gen-textarea-${dataType}`}
						className={styles['multi-lines-txt']}
					>
						{covertStringToList(textValue)}
					</List>
				)}
				{inputElement}
			</Stack>
			{button && dataType === 'input' && (
				<Stack direction="row" justifyContent="flex-end">
					<PromptSelect
						aiModelKey={button.aiModelKey}
						btnType={button?.type || 'primary'}
						onSelectPrompt={handlePromptChange}
						data-testid={button.testId}
						size="small"
						disabled={!textValue}
						loading={button.loading}
						clickHandler={handleButtonClick}
					>
						{button?.text && toSentenceCase(button?.text)}
					</PromptSelect>
				</Stack>
			)}
			{!button && (
				<Stack direction="row" justifyContent="space-between">
					<Typography className={styles['disclaimer-text']}>
						{DISCLAIMER}
					</Typography>
					<AIFeedback
						objectType={objectType}
						module={AI_SIMULATION_AND_EXPERIMENTATION}
						objectData={{
							prompt: inputData,
							response: textValue,
							reference: getReferenceCondition(),
						}}
						isDisabled={!textValue.length}
						shouldResetFeedback={shouldReset}
						allowSaveObjectType
					/>
				</Stack>
			)}
		</Stack>
	);
};

export default GeneratedTextArea;
